import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Megaphone, ArrowRight, Lock, Loader2, LogIn, Mail, KeyRound } from "lucide-react";
import { apiErrorMessage } from "../services/api";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { isStaff } from "../utils/roles";
import { MaintenancePage } from "./MaintenancePage";

export const AnnouncementBar = () => {
  const [ann, setAnn] = useState(null);
  useEffect(() => { api.get("/announcement").then(({ data }) => setAnn(data)).catch(() => {}); }, []);
  if (!ann?.enabled || !ann.text) return null;
  const external = /^https?:\/\//i.test(ann.link || "");
  const cta = ann.link ? (
    external
      ? <a href={ann.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline underline-offset-2 font-bold">{ann.label || "Voir"} <ArrowRight size={13} /></a>
      : <Link to={ann.link} className="inline-flex items-center gap-1 underline underline-offset-2 font-bold">{ann.label || "Voir"} <ArrowRight size={13} /></Link>
  ) : null;
  return (
    <div data-testid="announcement-bar" className="bg-[#E01F1F] text-white text-[13px] font-semibold">
      <div className="max-w-[1440px] mx-auto px-5 py-2 flex items-center justify-center gap-3 text-center">
        <Megaphone size={15} className="shrink-0" /> <span>{ann.text}</span> {cta}
      </div>
    </div>
  );
};

const AdminAccess = () => {
  const { login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { user: u } = await login(email, password);
      if (u && !isStaff(u)) { logout(); setError("Ce compte ne fait pas partie de l'équipe KTSauto."); }
    } catch (err) { setError(apiErrorMessage(err, "Identifiants incorrects.")); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button data-testid="maintenance-admin-login" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-[12px] font-semibold border border-white/10 hover:border-[#E01F1F]/60 rounded-lg px-3 py-1.5 transition-colors">
        <Lock size={13} /> Accès administrateur
      </button>
    );
  }
  return (
    <form onSubmit={submit} data-testid="maintenance-admin-form" className="w-full max-w-sm mx-auto bg-[#10131A] border border-[#222735] rounded-2xl p-5 text-left space-y-3 shadow-2xl shadow-black/60">
      <div className="flex items-center gap-2 text-slate-50 font-bold text-sm"><Lock size={15} className="text-[#FF3B3B]" /> Connexion administrateur</div>
      <div className="flex items-center gap-2 border-2 border-[#222735] rounded-lg px-3 focus-within:border-[#E01F1F]">
        <Mail size={15} className="text-slate-500" />
        <input data-testid="maintenance-admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@ktsauto.fr" className="flex-1 py-2.5 outline-none text-slate-50 bg-transparent text-[14px]" />
      </div>
      <div className="flex items-center gap-2 border-2 border-[#222735] rounded-lg px-3 focus-within:border-[#E01F1F]">
        <KeyRound size={15} className="text-slate-500" />
        <input data-testid="maintenance-admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe" className="flex-1 py-2.5 outline-none text-slate-50 bg-transparent text-[14px]" />
      </div>
      {error && <p data-testid="maintenance-admin-error" className="text-red-400 text-[13px] font-medium">{error}</p>}
      <button data-testid="maintenance-admin-submit" type="submit" disabled={busy} className="btn-orange w-full rounded-lg py-2.5 font-bold flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} Se connecter
      </button>
      <button type="button" onClick={() => setOpen(false)} className="w-full text-[12px] text-slate-500 hover:text-slate-300">Annuler</button>
    </form>
  );
};

export const MaintenanceGate = ({ children }) => {
  const { user, authReady } = useAuth();
  const { pathname } = useLocation();
  const [maint, setMaint] = useState(null);
  useEffect(() => { api.get("/maintenance").then(({ data }) => setMaint(data)).catch(() => setMaint({ enabled: false })); }, []);

  if (maint === null || !authReady) return children;
  const authRoute = ["/connexion", "/mot-de-passe-oublie", "/reset-password"].some((p) => pathname.startsWith(p));
  if (!maint.enabled || isStaff(user) || authRoute) return children;

  return <MaintenancePage maint={maint}><AdminAccess /></MaintenancePage>;
};
